import { PickWritable } from './types';

type ChildNode = Node | string;

type ElementStyles = Partial<PickWritable<CSSStyleDeclaration>>;
type ElementAttributes = {
  [name: string]: Partial<ElementStyles> | string | undefined;
  className?: string;
  id?: string;
  style?: Partial<ElementStyles>;
};

export const appendChildToElement = (
  parent: HTMLElement,
  child: ChildNode,
): HTMLElement => {
  if (typeof child === 'string') {
    parent.appendChild(document.createTextNode(child));
  } else {
    parent.appendChild(child);
  }

  return parent;
};

export const appendChildrenToElement = (
  element: HTMLElement,
  children: ChildNode[],
) => {
  children.forEach((child) => appendChildToElement(element, child));

  return element;
};

export const attachAttributesToElement = (
  element: HTMLElement,
  { className, id, style, ...attributes }: Partial<ElementAttributes>,
) => {
  if (typeof className === 'string') element.className = className;
  if (typeof id === 'string') element.id = id;
  if (typeof style === 'object' && style !== null)
    attachStylesToElement(element, style);

  for (const name in attributes) {
    const value = attributes[name];
    if (typeof value === 'string') {
      element.setAttribute(name, value);
    }
  }

  return element;
};

export const attachStylesToElement = (
  element: HTMLElement,
  styles: ElementStyles,
) => {
  for (const name in styles) {
    element.style[name] = styles[name] || '';
  }

  return element;
};

export const classNames = (
  ...classNames: (string | boolean | null | undefined)[]
) =>
  classNames
    .filter(
      (className) => typeof className === 'string' && className.length > 0,
    )
    .join(' ');

export const renderElement = <K extends keyof HTMLElementTagNameMap>(
  tagName: K,
  attributes: ElementAttributes = {},
  ...children: ChildNode[]
) =>
  appendChildrenToElement(
    attachAttributesToElement(document.createElement(tagName), attributes),
    children,
  );
